<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage User Stories</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'user_story.create', params: { id: 0 } }"
      new-button-text="New User Story"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      selectable
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'acceptance_criteria'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.acceptance_criteria"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'benefit'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.benefit"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'capability'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.capability"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'epic'">
            <o-boolean-select
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.epic"
            ></o-boolean-select>
          </span>
          <span v-if="field.key === 'priority'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.priority"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'project'">
            <o-form-select
              v-model="list_options.qbe.project"
              :options="optionOcomProjectItems"
              text-field="name"
              value-field="id"
              sort-field="name"
              blank-option-text=""
              :loading="optionOcomProjectWorking"
              @change="filter_changed"
              name="project"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'roles'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.roles"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'schedule_week'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.schedule_week"
              hide-time
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'split_from'">
            <o-form-select
              v-model="list_options.qbe.split_from"
              :options="optionUserStoryItems"
              text-field="action"
              value-field="id"
              sort-field="action"
              blank-option-text=""
              :loading="optionUserStoryWorking"
              @change="filter_changed"
              name="split_from"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'status'">
            <o-form-select
              v-model="list_options.qbe.status"
              :options="optionCodeUserStoryStatusItems"
              text-field="description"
              value-field="id"
              sort-field="description"
              blank-option-text=""
              :loading="optionCodeUserStoryStatusWorking"
              @change="filter_changed"
              name="status"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'story_points'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.story_points"
            ></b-form-input>
          </span>
        </td>
      </template>
      <template slot="cell(acceptance_criteria)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'acceptance_criteria'"
        >
          <span v-html="item.acceptance_criteria"></span>
        </o-link-cell>
      </template>
      <template slot="cell(benefit)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'benefit'"
        >
          <span v-html="item.benefit"></span>
        </o-link-cell>
      </template>
      <template slot="cell(capability)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'capability'"
        >
          <span v-html="item.capability"></span>
        </o-link-cell>
      </template>
      <template slot="cell(epic)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'epic'"
        >
          <boolean-display :value="item.epic"></boolean-display>
        </o-link-cell>
      </template>
      <template slot="cell(priority)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'priority'"
        >
          <number-display :value="item.priority" :decimals="0" />
        </o-link-cell>
      </template>
      <template slot="cell(project)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'project'"
        >
          <lookup-display
            :list="optionOcomProjectItemsById"
            :value="item.project"
            display-field="name"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(roles)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'roles'"
        >
          <default-display :value="item.roles"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(schedule_week)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'schedule_week'"
        >
          <date-display
            :value="item.schedule_week"
            format="DD/MM/YYYY"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(split_from)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'split_from'"
        >
          <lookup-display
            :list="optionUserStoryItemsById"
            :value="item.split_from"
            display-field="action"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(status)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'status'"
        >
          <lookup-display
            :list="optionCodeUserStoryStatusItemsById"
            :value="item.status"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(story_points)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'user_story.edit', params: { id: item.id } }"
          :when="first_column_key === 'story_points'"
        >
          <number-display :value="item.story_points" :decimals="2" />
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'user_story.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("UserStory", "user_story"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("UserStory"),
    CreateCodeTableFetchMixin([
      "OcomProject",
      "UserStory",
      "CodeUserStoryStatus",
    ]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.UserStory,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.UserStory),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        OcomProject: {
          options: this.optionOcomProjectItems,
          textField: "name",
          valueField: "id",
        },
        UserStory: {
          options: this.optionUserStoryItems,
          textField: "action",
          valueField: "id",
        },
        CodeUserStoryStatus: {
          options: this.optionCodeUserStoryStatusItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "project",
        "roles",
        "capability",
        "benefit",
        "epic",
        "story_points",
        "priority",
        "schedule_week",
        "status",
        "acceptance_criteria",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
  },
};
</script>
